export interface CodePointRange {
  start: number;
  end: number;
}

function assertIntegerOffset(offset: number, maximum: number, unit: string): void {
  if (!Number.isInteger(offset) || offset < 0 || offset > maximum) {
    throw new RangeError(`${unit} offset is out of bounds`);
  }
}

export function utf16ToCodePointOffset(text: string, utf16Offset: number): number {
  assertIntegerOffset(utf16Offset, text.length, 'UTF-16');

  if (
    utf16Offset > 0
    && utf16Offset < text.length
    && /[\uD800-\uDBFF]/.test(text[utf16Offset - 1])
    && /[\uDC00-\uDFFF]/.test(text[utf16Offset])
  ) {
    throw new RangeError('UTF-16 offset splits a surrogate pair');
  }

  return Array.from(text.slice(0, utf16Offset)).length;
}

export function codePointToUTF16Offset(text: string, codePointOffset: number): number {
  const codePoints = Array.from(text);
  assertIntegerOffset(codePointOffset, codePoints.length, 'Code point');
  return codePoints.slice(0, codePointOffset).join('').length;
}

function isWithinRoot(root: Node, node: Node): boolean {
  return root === node || root.contains(node);
}

function utf16OffsetBeforeNode(root: Node, target: Node): number {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let offset = 0;
  let textNode = walker.nextNode();

  while (textNode) {
    if (textNode === target || target.contains(textNode)) {
      return offset;
    }

    const position = textNode.compareDocumentPosition(target);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      offset += textNode.textContent?.length ?? 0;
    } else {
      return offset;
    }
    textNode = walker.nextNode();
  }

  return offset;
}

function boundaryToUTF16Offset(root: Node, container: Node, offset: number): number {
  if (!isWithinRoot(root, container)) {
    throw new RangeError('Selection boundary is outside the root');
  }

  if (container.nodeType === Node.TEXT_NODE) {
    const text = container.textContent ?? '';
    assertIntegerOffset(offset, text.length, 'DOM');
    return utf16OffsetBeforeNode(root, container) + offset;
  }

  assertIntegerOffset(offset, container.childNodes.length, 'DOM');
  if (offset < container.childNodes.length) {
    return utf16OffsetBeforeNode(root, container.childNodes[offset]);
  }

  return utf16OffsetBeforeNode(root, container) + (container.textContent?.length ?? 0);
}

export function selectionToCodePointRange(root: Node, range: Range): CodePointRange {
  const text = root.textContent ?? '';
  const startUTF16 = boundaryToUTF16Offset(root, range.startContainer, range.startOffset);
  const endUTF16 = boundaryToUTF16Offset(root, range.endContainer, range.endOffset);

  return {
    start: utf16ToCodePointOffset(text, startUTF16),
    end: utf16ToCodePointOffset(text, endUTF16),
  };
}
